import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Flame, Menu, X, LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <Flame className="w-8 h-8 text-amber-600 group-hover:text-amber-700 transition-colors" />
            <span className="text-xl font-bold text-slate-900">VikingFuneralPyres</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive('/') ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/how-it-works"
              className={`text-sm font-medium transition-colors ${
                isActive('/how-it-works') ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'
              }`}
            >
              How It Works
            </Link>
            <Link
              to="/gallery"
              className={`text-sm font-medium transition-colors ${
                isActive('/gallery') ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'
              }`}
            >
              Gallery
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  to="/my-funerals"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/my-funerals') ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'
                  }`}
                >
                  My Funerals
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className={`text-sm font-medium transition-colors ${
                      isActive('/admin') ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'
                    }`}
                  >
                    Admin
                  </Link>
                )}
              </>
            )}
            {isAuthenticated ? (
              <>
                <Link to="/create">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    Create Funeral
                  </Button>
                </Link>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <User className="w-4 h-4" />
                    <span>{user?.name}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout}
                    className="text-slate-700 hover:text-amber-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-700 hover:text-amber-600">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium transition-colors ${
                  isActive('/') ? 'text-amber-600' : 'text-slate-700'
                }`}
              >
                Home
              </Link>
              <Link
                to="/how-it-works"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium transition-colors ${
                  isActive('/how-it-works') ? 'text-amber-600' : 'text-slate-700'
                }`}
              >
                How It Works
              </Link>
              <Link
                to="/gallery"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium transition-colors ${
                  isActive('/gallery') ? 'text-amber-600' : 'text-slate-700'
                }`}
              >
                Gallery
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/my-funerals"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-colors ${
                      isActive('/my-funerals') ? 'text-amber-600' : 'text-slate-700'
                    }`}
                  >
                    My Funerals
                  </Link>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-sm font-medium transition-colors ${
                        isActive('/admin') ? 'text-amber-600' : 'text-slate-700'
                      }`}
                    >
                      Admin
                    </Link>
                  )}
                </>
              )}
              {isAuthenticated ? (
                <>
                  <Link to="/create" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="bg-amber-600 hover:bg-amber-700 w-full">
                      Create Funeral
                    </Button>
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-slate-700 px-2">
                    <User className="w-4 h-4" />
                    <span>{user?.name}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-slate-700 hover:text-amber-600 justify-start"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="text-slate-700 hover:text-amber-600 w-full justify-start">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="bg-amber-600 hover:bg-amber-700 w-full">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;